import { Box, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import classNames from 'classnames';
import { Fragment } from 'react';
import { useSelector } from 'react-redux';
import Pills from '/@/components/pills';
import ProductBenefits from '/@/components/product-benefits';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import { getSessionCountry, getSessionLanguage } from '/@/store/session/selectors';
import Title from '/@/v3/components/title';
import noImg from './assets/no-img.jpg?url';
import styles from './styles.module.css';

const CatalogProduct = ({ brand, profile, handleClick, type, cropProtection }) => {
    const country = useSelector(getSessionCountry);
    const lang = useSelector(getSessionLanguage);
    const isSoyBR = country.code === 'BR' && type === 'soy';

    const { id, shortDescription } = profile;

    const descriptionsCutter =
        (maxLength = 180, maxItems = 3) =>
        (obj, el) => {
            const description = el.description;
            const possibleNewLength = obj.length + description?.length;

            const maxLengthAllow = possibleNewLength <= maxLength;
            const lastItemAllow = obj.length < maxLength && possibleNewLength >= maxLength + 10; // No acortar frases muy cortas

            if (obj.array.length >= maxItems) return obj;

            if (maxLengthAllow) {
                obj.array.push(el);
                obj.length += description ? description.length : 0;
            } else if (lastItemAllow) {
                el[lang] = `${description.slice(0, maxLength - obj.length)}...`;
                obj.array.push(el);
                obj.length += maxLength;
            }
            return obj;
        };

    let CPDescriptions;
    let description;

    if (type === 'crop_protection') {
        const benefits = JSON.parse(JSON.stringify(profile.valueProposal));

        CPDescriptions = benefits
            .map((el) => ({
                description: _(el.text),
            }))
            .sort((a, b) => a.description.length - b.description.length)
            .reduce(descriptionsCutter(100, 2), {
                array: [],
                length: 0,
            }).array;
    } else {
        const benefits = JSON.parse(JSON.stringify(profile.notes));

        description = benefits
            .map((el) => ({
                description: _(el),
            }))
            .sort((a, b) => a.description?.length - b.description?.length)
            .reduce(descriptionsCutter(60, 3), {
                array: [],
                length: 0,
            }).array;
    }

    const DK = profile.technologies && profile.technologies.name === 'DKProtect';
    const products = { ...profile, DK };

    const getLabelConfig = () => {
        if (profile.newProduct) return { type: 'newProduct', text: _('Nuevo producto') };
    };

    const labelConfig = getLabelConfig(profile);

    let refugeBenefits =
        profile.notes &&
        profile.notes.map((not) => {
            return not.text;
        });

    let DKProtect =
        products.technologies &&
        products.technologies.map((tec) => {
            return tec.codename && tec.codename;
        });
    DKProtect = DKProtect && DKProtect.includes('DKProtect');
    refugeBenefits = refugeBenefits && refugeBenefits.slice(0, 3);

    const CPBenefits = cropProtection && CPDescriptions;

    const newImage = new URL(`./assets/new-${lang}.svg`, import.meta.url).href;

    return (
        <div className={styles.root}>
            {labelConfig && <div style={{ backgroundImage: `url(${newImage})` }} className={styles.new} />}
            <div className={classNames(styles.content)}>
                <div
                    className={classNames(
                        cropProtection && styles.cropProtectionProduct,
                        styles.product,
                        styles[brand.type],
                        styles[`theme-${brand.theme}`]
                    )}
                >
                    {cropProtection ? (
                        <Fragment>
                            <div className={styles.logoContainer}>
                                {profile.image && (
                                    <div
                                        className={styles.logo}
                                        style={{ backgroundImage: `url('${profile.image}')` }}
                                    />
                                )}
                            </div>
                            <div className={styles.contentContainerCp}>
                                <Pills type={profile.type} crops={profile?.crops || []} />
                                <div className={styles.textContainer}>
                                    {shortDescription && (
                                        <Typography
                                            variant='body2'
                                            sx={{
                                                fontWeight: 600,
                                                p: 1,
                                            }}
                                            color='primary'
                                        >
                                            {_(shortDescription)}
                                        </Typography>
                                    )}
                                    <div>
                                        <ProductBenefits benefits={CPBenefits} />
                                    </div>
                                </div>
                            </div>
                        </Fragment>
                    ) : (
                        <div className={styles.contentContainer}>
                            {cropProtection ? null : country.code === 'MX' ? (
                                <div className={styles.mxCardContainer}>
                                    <div
                                        className={styles.mxBackgroundImage}
                                        style={{
                                            backgroundImage: `url('${profile.catalogImage}')`,
                                        }}
                                    />
                                    <img className={styles.mxImage} src={profile.catalogImage} />
                                </div>
                            ) : (
                                <div
                                    className={classNames(styles.header, DKProtect && labelConfig && styles.newHeader)}
                                >
                                    <Box
                                        sx={{
                                            p: 1,
                                            minHeight: 50,
                                            display: 'flex',
                                            borderRadius: 2,
                                            width: '100%',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 1,
                                            backgroundColor: (t) => t.palette.primary[200],
                                            ...(DKProtect && {
                                                width: '55%',
                                                padding: 0,
                                                position: 'relative',
                                                height: '0px',
                                                borderRadius: 0,
                                                borderRight: '60px solid transparent',
                                                borderLeft: '0px solid transparent',
                                                backgroundColor: 'white',
                                                borderBottom: (t) => `50px solid ${t.palette.primary[100]}`,
                                            }),
                                        }}
                                    >
                                        <Box
                                            sx={
                                                DKProtect && {
                                                    position: 'absolute',
                                                    px: 1,
                                                    height: '50px',
                                                    top: '50%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                }
                                            }
                                        >
                                            <Title size='md' theme={settings.app.theme} bolder>
                                                {_(profile.catalogName || profile.name)}
                                            </Title>
                                        </Box>

                                        {DKProtect && !labelConfig && (
                                            <div className={classNames(styles.dkLogo, labelConfig && styles.dkNew)} />
                                        )}
                                    </Box>
                                    {(DKProtect || labelConfig) && (
                                        <div className={classNames(styles.dkLogo, labelConfig && styles.dkNew)} />
                                    )}
                                </div>
                            )}
                            {shortDescription && (
                                <Typography
                                    variant='body2'
                                    sx={{
                                        fontWeight: 600,
                                        p: 1,
                                        color: (t) => t.palette.primary.main,
                                    }}
                                >
                                    {_(shortDescription)}
                                </Typography>
                            )}
                            <div
                                className={classNames(
                                    styles.description,
                                    country.code === 'MX' ? styles.descriptionMx : styles.descriptionHeight
                                )}
                            >
                                {isSoyBR && Boolean(profile.catalogImage) ? (
                                    <div className={styles.mxCardContainer}>
                                        <div
                                            className={styles.mxBackgroundImage}
                                            style={{
                                                backgroundImage: `url('${profile.catalogImage}')`,
                                            }}
                                        />
                                        <img
                                            className={styles.mxImage}
                                            src={profile.catalogImage || noImg}
                                            alt='Soy Product Image'
                                        />
                                    </div>
                                ) : (
                                    <ProductBenefits
                                        refuge={type === 'refuge'}
                                        benefits={type === 'refuge' ? refugeBenefits : description}
                                    />
                                )}
                            </div>
                        </div>
                    )}
                    <Button variant='contained' onClick={() => handleClick(id)}>
                        {_('Ver más')}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default CatalogProduct;
